import type { LucideIcon } from 'lucide-react';
import {
  Megaphone,
  UserPlus,
  ShieldPlus,
  ClipboardPlus,
  FilePlus2,
  FileCheck2,
  Upload,
  ScanLine,
} from 'lucide-react';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { isFileSystemAccessSupported } from '@features/document-capture/lib/scanWatcher';

/**
 * The 1–2 verbs each module exists for (M3 · 86d44z1xm).
 *
 * ⛔ THE RULE THAT MATTERS: a quick action that silently does nothing on arrival is worse than not
 * offering it. Every entry here either navigates somewhere that performs the verb, or carries an
 * intent marker the destination page consumes. None of them "navigate and hope".
 *
 * 🔑 Gating is the AND of the VERB and the DESTINATION ROUTE'S OWN GATE. Three of these are the
 * trap: "Post announcement" needs `announcement.create` but its page enforces `announcement.view`;
 * "New quotation" and "New sales order" are the same shape. Gating on the create permission alone
 * shows the shortcut to a user the router will silently bounce to `/`.
 *
 * ⚖️ Owner decision 4 (2026-08-26) OVERRIDES this ticket's `moduleCode` column: disabled modules
 * SHOW NORMALLY, so nothing here consults `useSystemModuleStore`. A quick action for a disabled
 * module navigates and lands on `ModuleGuard`'s inline notice — exactly what the rail button beside
 * it does, and exactly what happens today. Document Capture ships `IsEnabled = false`, so its two
 * actions will do that on day one; that is accepted, not an oversight.
 */
export interface QuickAction {
  id: string;
  /** Which module panel shows it. Matches `MenuEntry.id`. */
  moduleId: string;
  icon: LucideIcon;
  label: string;
  /** Tooltip copy — says what will happen, since the label is a verb with no object. */
  hint: string;
  href: string;
  /** EVERY one required: the verb AND the destination route's own permission gate. */
  requiresAll: string[];
  /** At least ONE required — mirrors a destination the router gates with `anyOf`. */
  requiresAny?: string[];
  /** Runtime capability gate. When false the action is HIDDEN, never disabled. */
  isSupported?: () => boolean;
}

/**
 * Intent markers. One mechanism for every action whose verb cannot be expressed as a plain URL,
 * consumed by a one-shot effect on the destination that strips the marker with
 * `setSearchParams(next, { replace: true })`.
 *
 * The reference implementation already shipped: `?new=1` at QuotationWorkspacePage.tsx:186-192.
 */
export const INTENT_PARAM = 'action';

export const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'post-announcement',
    moduleId: 'administration',
    icon: Megaphone,
    label: 'Post announcement',
    hint: 'Open the announcement composer',
    // There is NO composer route — it is a Dialog driven by page-local state, so this is an intent.
    href: `/administration/announcements?${INTENT_PARAM}=new`,
    requiresAll: [PERMISSIONS.ANNOUNCEMENT_CREATE, PERMISSIONS.ANNOUNCEMENT_VIEW],
  },
  {
    id: 'add-user',
    moduleId: 'administration',
    icon: UserPlus,
    label: 'Add user',
    hint: 'Create a new user account',
    href: '/administration/users/new',
    // Verb and destination gate are the same permission here — the route enforces USER_CREATE_USER.
    requiresAll: [PERMISSIONS.USER_CREATE_USER],
  },
  {
    id: 'add-role',
    moduleId: 'administration',
    icon: ShieldPlus,
    label: 'Add role',
    hint: 'Create a new role',
    href: '/administration/authorization/roles/new',
    requiresAll: [PERMISSIONS.AUTH_CREATE_ROLE],
  },
  {
    id: 'new-picklist',
    moduleId: 'mro',
    icon: ClipboardPlus,
    label: 'New picklist',
    hint: 'Start a picklist from material requests',
    href: '/mro/issuance/create',
    requiresAll: [PERMISSIONS.ISSUANCE_CREATE_PICKLIST],
  },
  {
    id: 'new-quotation',
    moduleId: 'sales',
    icon: FilePlus2,
    label: 'New quotation',
    hint: 'Open the workspace with a blank quotation',
    // ?new=1 already exists and is consumed + stripped by QuotationWorkspacePage.
    href: '/quotations/workspace?new=1',
    requiresAll: [PERMISSIONS.QUOTATIONS_CREATE_QUOTATION],
    requiresAny: [
      PERMISSIONS.QUOTATIONS_VIEW_QUOTATION,
      PERMISSIONS.QUOTATIONS_VIEW_ALL_QUOTATIONS,
    ],
  },
  {
    id: 'new-sales-order',
    moduleId: 'sales',
    icon: FileCheck2,
    label: 'New sales order',
    hint: 'Open the workspace and pick a forward order',
    // ?new=1 did NOT exist here — M3 adds the handler, mirroring quotations.
    href: '/sales-orders/workspace?new=1',
    requiresAll: [
      PERMISSIONS.SALES_ORDERS_CREATE_SALES_ORDER,
      PERMISSIONS.SALES_ORDERS_VIEW_SALES_ORDER,
    ],
  },
  {
    id: 'upload-document',
    moduleId: 'services',
    icon: Upload,
    label: 'Upload document',
    hint: 'Go to Document Capture and drop files in',
    // A file input's .click() needs transient user activation just as the directory picker does,
    // so this cannot fire itself after navigating either — the page primes the dropzone instead.
    href: `/documents?${INTENT_PARAM}=upload`,
    // `docscan.upload` is itself one of the /documents anyOf gate, so the verb grants the
    // destination — no second permission needed.
    requiresAll: [PERMISSIONS.DOCSCAN_UPLOAD],
  },
  {
    id: 'open-scan-station',
    moduleId: 'services',
    icon: ScanLine,
    label: 'Open Scan Station',
    hint: 'Go to Document Capture and choose a watch folder',
    /* ⛔ This action CANNOT open the folder picker itself. `showDirectoryPicker` requires
       TRANSIENT USER ACTIVATION, and an effect that runs after navigation has none — the browser
       refuses the picker. Owner ruling 2026-08-26: land on /documents and let the page surface a
       PRIMED control the user clicks, which preserves activation. */
    href: `/documents?${INTENT_PARAM}=scan`,
    // The whole station sits behind `canUpload`; without this the watcher is never constructed, so
    // the action would be a guaranteed no-op for exactly the users shown it.
    requiresAll: [PERMISSIONS.DOCSCAN_UPLOAD],
    // Chromium-only. HIDE on unsupported browsers, never disable.
    isSupported: isFileSystemAccessSupported,
  },
];
